import { useEffect, useMemo, useState } from 'react';
import throttle from 'lodash/throttle';
import includes from 'lodash/includes';

import { Currencies } from '../../../../../../types';
import {
  InvoiceItemsViewFields,
  InvoiceItemsViewInvoice
} from '../../InvoiceItemsView.types';
import {
  FetchInvoicesCacheKeys,
  InvoiceNanoID,
  InvoiceStatuses
} from '../../../../invoicesTypes';
import {
  ProformaInvoiceFormGroupByMode,
  ProformaInvoiceFormViewMode
} from '../../../../../teams/components/forms/ProformaInvoiceForm/ProformaInvoiceForm.types';
import { InvoiceItemsViewContextType } from '../useInvoiceItemsViewContext';

import { useCurrentUser } from '../../../../../../auth/hooks/useAuth';
import { useInvoiceItemsViewViewModes } from '../useInvoiceItemsViewViewModes';
import { useInvoiceItemsViewFields } from '../useInvoiceItemsViewFields';
import { useInvoiceItemsViewItems } from '../useInvoiceItemsViewItems';
import { useInvoiceItemsViewComputedFields } from '../useInvoiceItemsViewComputedFields';
import { useInvoiceItemsViewHandlers } from '../useInvoiceItemsViewHandlers';
import { useInvoiceItemsViewPreparedItems } from '../useInvoiceItemsViewPreparedItems';
import { useInvoiceItemsViewCheckable } from '../useInvoiceItemsViewCheckable';
import { useCacheSyncInvoice } from '../../../../hooks/useCacheSyncInvoice';

import { getCurrencyPrefix } from '../../../../../currencyRates/utils/getCurrencyPrefix';

import { GroupedItemsPermissions } from '../../../../../groupedItems/groupedItemsConstants';
import { GroupedItemCache } from '../../../../../groupedItems/GroupedItemCache';
import {
  bannedInvoiceStatuses,
  InvoicesPermissions
} from '../../../../invoicesConstants';

interface InvoiceItemsViewOptions {
  invoice?: InvoiceItemsViewInvoice;
  invoiceNanoId: InvoiceNanoID;
  thirdPersonView?: boolean;
  self?: boolean;
  cacheKeys?: FetchInvoicesCacheKeys;
  readonly?: boolean;
}

function useInvoiceItemsView({
  invoice,
  invoiceNanoId,
  thirdPersonView,
  self,
  cacheKeys: invoiceCacheKeys,
  readonly
}: InvoiceItemsViewOptions) {
  const currentUser = useCurrentUser();

  const canEditPaidInvoice = currentUser.hasPermissions(
    InvoicesPermissions.CHANGE_PAID_INVOICE_FIELDS
  );

  const editableInvoiceStatus = !(
    includes(bannedInvoiceStatuses, invoice?.status) || invoice?.processingAt
  );

  const cacheApiEnabled = currentUser.hasPermissions(
    GroupedItemsPermissions.READ_CACHE_INVOICE_GROUPED_ITEMS_QUERY
  );

  const cacheSyncEnabled = currentUser.hasPermissions(
    GroupedItemsPermissions.SYNC_CACHE_INVOICE_GROUPED_ITEMS
  );

  const [hasSubprojects, setHasSubprojects] = useState(
    invoice?.viewSettings?.viewMode === ProformaInvoiceFormViewMode.GROUPS
  );

  const { viewModeOptions, groupByOptions } = useInvoiceItemsViewViewModes({
    hasSubprojects
  });

  const {
    control,
    // errors,
    setValue,
    handleViewModeChange,
    watchFields,
    registerFields
  } = useInvoiceItemsViewFields({
    defaultCurrency: invoice?.selectedCurrency,
    defaultViewMode:
      invoice?.viewSettings?.viewMode || ProformaInvoiceFormViewMode.INVOICES,
    defaultGroupBy:
      invoice?.viewSettings?.groupBy || ProformaInvoiceFormGroupByMode.PROJECT,
    defaultEnableGrouping: invoice?.viewSettings?.enableGrouping ?? true,
    groupByOptions
  });

  const currencyPrefix = getCurrencyPrefix(invoice?.selectedCurrency);

  const { handleCacheSyncInvoice } = useCacheSyncInvoice({
    cacheKeys: [GroupedItemCache.invoiceGroupedItemsCacheKey(invoice?.nanoId)]
  });

  const throttledCacheSyncInvoice = useMemo(
    () => throttle(handleCacheSyncInvoice, 5000, { trailing: false }),
    [handleCacheSyncInvoice]
  );

  useEffect(() => {
    return () => {
      throttledCacheSyncInvoice.cancel();
    };
  }, [throttledCacheSyncInvoice]);

  const {
    cacheKeys: groupedItemsCacheKeys,
    groupedItemsFetched,
    groupedItems,
    groupedItemsLoading,
    groupedItemsError
  } = useInvoiceItemsViewItems({
    invoiceNanoId: invoice?.nanoId,
    currencyRateId: invoice?.currencyRateId,
    currency: invoice?.selectedCurrency,
    viewMode: watchFields.watchViewMode,
    groupBy: watchFields.watchGroupBy,
    enableGrouping: watchFields.watchEnableGrouping,
    cacheApiEnabled,
    onSuccess: (data) => {
      const meta = data?.pages?.[0]?.groupedItems?.meta;

      if (meta && invoice) {
        const invoiceTotalCurrency =
          invoice.status === InvoiceStatuses.DRAFT
            ? invoice.invoiceTempTotalConverted
            : invoice.invoiceTotalConverted;

        const metaTotalCurrency = meta.convertedTotalPrice;

        if (
          cacheSyncEnabled &&
          invoiceTotalCurrency !== metaTotalCurrency &&
          invoice.nanoId
        ) {
          throttledCacheSyncInvoice({
            invoiceId: invoice.nanoId,
            async: true,
            withItems: true,
            withTask: true,
            withSmartContracts: true,
            withParentInvoice: true,
            withChildInvoices: true,
            withAncestorProjects: true
          });
        }
      }
    }
  });

  const cacheKeys = useMemo(
    () => [...invoiceCacheKeys, ...groupedItemsCacheKeys],
    [invoiceCacheKeys, groupedItemsCacheKeys]
  );

  const { preparedItems, flatItems } = useInvoiceItemsViewPreparedItems({
    items: groupedItems,
    viewMode: watchFields.watchViewMode,
    groupBy: watchFields.watchGroupBy,
    enableGrouping: watchFields.watchEnableGrouping
  });

  const {
    checkedHash,
    checkedAll,
    checkedItems,
    checkedPartial,
    handleSetCheckedIds,
    handleCheckAll,
    handleUncheckAll
  } = useInvoiceItemsViewCheckable({
    items: flatItems
  });

  const {
    invoiceStatus,
    payments,
    prepayment,
    prepaymentAmount,
    amountDue,
    invoiceTotal,
    checkedItemsTotal,
    scStatuses
  } = useInvoiceItemsViewComputedFields({
    invoice,
    checkedItems,
    items: flatItems
  });

  const {
    handleChangeCurrency,
    updateViewSettingsInvoiceLoading,
    updateViewSettingsInvoiceError,
    changeCurrencyLoading,
    changeCurrencyError
  } = useInvoiceItemsViewHandlers({
    invoice,
    cacheKeys,
    viewMode: watchFields.watchViewMode,
    groupBy: watchFields.watchGroupBy,
    enableGrouping: watchFields.watchEnableGrouping,
    groupedItemsLoading,
    setHasSubprojects,
    flatItems
  });

  useEffect(() => {
    if (!groupedItemsFetched) return;

    const isViewModeValid = viewModeOptions.some(
      (opt) => opt.value === watchFields.watchViewMode
    );
    if (!isViewModeValid && viewModeOptions.length > 0) {
      setValue(
        InvoiceItemsViewFields.VIEW_MODE,
        viewModeOptions[0]?.value as ProformaInvoiceFormViewMode
      );
    }
  }, [
    groupedItemsFetched,
    setValue,
    viewModeOptions,
    watchFields.watchViewMode
  ]);

  const context: InvoiceItemsViewContextType = {
    invoice,
    invoiceNanoId,
    control,
    // errors,
    items: preparedItems,
    groupedItemsFetched,
    groupedItemsLoading,
    groupedItemsError,
    updateViewSettingsInvoiceLoading,
    updateViewSettingsInvoiceError,
    changeCurrencyLoading,
    changeCurrencyError,
    checkedAll,
    checkedPartial,
    checkedHash,
    checkedItems,
    handleCheckAll,
    handleUncheckAll,
    handleSetCheckedIds,
    cacheKeys,
    viewModeOptions,
    groupByOptions,
    watchFields,
    registerFields,
    currentUser,
    currencyPrefix,
    invoiceStatus,
    payments,
    prepayment,
    prepaymentAmount,
    amountDue,
    invoiceTotal,
    checkedItemsTotal,
    thirdPersonView,
    self,
    handleChangeCurrency,
    handleViewModeChange,
    isDraft: invoice?.clientStatus === InvoiceStatuses.DRAFT,
    readonly,
    canEditInvoice:
      !readonly &&
      (invoice?.status === InvoiceStatuses.PAID
        ? canEditPaidInvoice
        : editableInvoiceStatus),
    selectedCurrency: invoice?.selectedCurrency || Currencies.USD,
    scStatuses
  };

  return context;
}

export default useInvoiceItemsView;
